import React, { useState } from 'react';
import { Search, Filter, CheckCircle, XCircle, MessageCircle, User, Calendar, DollarSign } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import AdminLayout from '../../components/admin/AdminLayout';
import { useLeaseStore, type LeaseStatus } from '../../stores/leaseStore';
import { useUserStore } from '../../stores/userStore';
import { useMessageStore } from '../../stores/messageStore';
import { formatPrice, formatDate } from '../../utils/formatters';
import { getStatusColor, getStatusText } from '../../utils/statusUtils';

const AdminApplications = () => {
  const navigate = useNavigate();
  const { requests, updateRequestStatus } = useLeaseStore();
  const { users } = useUserStore();
  const { createConversation } = useMessageStore();
  const [searchTerm, setSearchTerm] = useState('');
  const [filter, setFilter] = useState<'all' | LeaseStatus>('all');
  const [alert, setAlert] = useState<{ type: 'success' | 'error', message: string } | null>(null);

  const showAlert = (message: string, type: 'success' | 'error' = 'success') => {
    setAlert({ message, type });
    setTimeout(() => setAlert(null), 3000);
  };

  const filteredRequests = requests.filter(request => {
    const matchesSearch = request.applicant.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         request.listing.title.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesFilter = filter === 'all' || request.status === filter;
    return matchesSearch && matchesFilter;
  });

  const handleApprove = async (requestId: string) => {
    try {
      updateRequestStatus(requestId, { status: 'approved' });
      showAlert('Application approved successfully');
    } catch (error) {
      showAlert('Error approving application', 'error');
    }
  };

  const handleReject = async (requestId: string) => {
    try {
      updateRequestStatus(requestId, { status: 'rejected' });
      showAlert('Application rejected');
    } catch (error) {
      showAlert('Error rejecting application', 'error');
    }
  };

  const handleMessage = (request: any) => {
    const conversationId = createConversation({
      participants: ['admin', request.applicant.id],
      recipientId: request.applicant.id,
      recipientName: request.applicant.name,
      listingId: request.listingId,
      listingTitle: request.listing.title
    });

    navigate(`/messages?conversation=${conversationId}`);
  };

  return (
    <AdminLayout>
      <div className="space-y-6">
        {alert && (
          <div className={`p-4 rounded-lg ${
            alert.type === 'error' ? 'bg-red-50 text-red-800' : 'bg-green-50 text-green-800'
          }`}>
            {alert.message}
          </div>
        )}

        {/* Header Stats */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          <div className="bg-white rounded-lg shadow-sm p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600">Pending Applications</p>
                <p className="text-2xl font-bold text-yellow-600">
                  {requests.filter(r => r.status === 'pending').length}
                </p>
              </div>
              <div className="h-12 w-12 bg-yellow-100 rounded-lg flex items-center justify-center">
                <Calendar className="h-6 w-6 text-yellow-600" />
              </div>
            </div>
          </div>

          <div className="bg-white rounded-lg shadow-sm p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600">Approved</p>
                <p className="text-2xl font-bold text-green-600">
                  {requests.filter(r => r.status === 'approved').length}
                </p>
              </div>
              <div className="h-12 w-12 bg-green-100 rounded-lg flex items-center justify-center">
                <CheckCircle className="h-6 w-6 text-green-600" />
              </div>
            </div>
          </div>

          <div className="bg-white rounded-lg shadow-sm p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600">Total Applications</p>
                <p className="text-2xl font-bold text-indigo-600">
                  {requests.length}
                </p>
              </div>
              <div className="h-12 w-12 bg-indigo-100 rounded-lg flex items-center justify-center">
                <User className="h-6 w-6 text-indigo-600" />
              </div>
            </div>
          </div>
        </div>

        {/* Filters */}
        <div className="flex flex-col sm:flex-row gap-4">
          <div className="flex-1">
            <div className="relative">
              <Search className="absolute left-3 top-2.5 h-5 w-5 text-gray-400" />
              <input
                type="text"
                placeholder="Search applications..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-indigo-500 focus:border-indigo-500"
              />
            </div>
          </div>
          <div className="relative">
            <div className="relative">
              <Filter className="absolute left-3 top-2.5 h-5 w-5 text-gray-400" />
              <select
                value={filter}
                onChange={(e) => setFilter(e.target.value as 'all' | LeaseStatus)}
                className="appearance-none w-full pl-10 pr-10 py-2 border border-gray-300 rounded-lg focus:ring-indigo-500 focus:border-indigo-500 bg-white cursor-pointer"
              >
                <option value="all">All Applications</option>
                <option value="pending">Pending Review</option>
                <option value="approved">Approved</option>
                <option value="rejected">Rejected</option>
              </select>
              <div className="absolute inset-y-0 right-0 flex items-center pr-2 pointer-events-none">
                <svg className="h-5 w-5 text-gray-400" viewBox="0 0 20 20" fill="currentColor">
                  <path fillRule="evenodd" d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z" clipRule="evenodd" />
                </svg>
              </div>
            </div>
          </div>
        </div>

        {/* Applications List */}
        <div className="space-y-6">
          {filteredRequests.map((request) => (
            <div key={request.id} className="bg-white rounded-lg shadow-sm overflow-hidden">
              <div className="flex flex-col md:flex-row">
                {/* Property Image */}
                <div className="w-full md:w-64 h-48 md:h-auto relative">
                  <img
                    src={request.listing.image}
                    alt={request.listing.title}
                    className="w-full h-full object-cover"
                  />
                </div>

                {/* Application Details */}
                <div className="flex-1 p-6">
                  <div className="flex justify-between items-start mb-4">
                    <div>
                      <h3 className="text-lg font-medium text-gray-900">
                        {request.applicant.name}
                      </h3>
                      <p className="text-sm text-gray-500">{request.applicant.email}</p>
                    </div>
                    <span className={`px-3 py-1 rounded-full text-xs font-medium ${getStatusColor(request.status)}`}>
                      {getStatusText(request.status)}
                    </span>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                    <div>
                      <p className="text-sm text-gray-500">Property</p>
                      <p className="text-gray-900">{request.listing.title}</p>
                      <p className="text-sm text-gray-500">{request.listing.location}</p>
                    </div>

                    <div>
                      <p className="text-sm text-gray-500">Monthly Rent</p>
                      <p className="text-gray-900">{formatPrice(request.listing.price)}</p>
                    </div>

                    <div>
                      <p className="text-sm text-gray-500">Move-in Period</p>
                      <p className="text-gray-900">
                        {formatDate(request.moveInDate)} - {formatDate(request.moveOutDate)}
                      </p>
                    </div>

                    <div>
                      <p className="text-sm text-gray-500">Monthly Income</p>
                      <p className="text-gray-900">{formatPrice(request.applicant.monthlyIncome)}</p>
                      <p className="text-sm text-gray-500">
                        {(request.applicant.monthlyIncome / request.listing.price).toFixed(1)}x rent
                      </p>
                    </div>
                  </div>

                  {request.message && (
                    <div className="mb-4">
                      <p className="text-sm font-medium text-gray-500 mb-2">Message</p>
                      <p className="text-gray-600 bg-gray-50 rounded-lg p-4">
                        {request.message}
                      </p>
                    </div>
                  )}

                  {request.status === 'pending' && (
                    <div className="flex justify-end space-x-3">
                      <button 
                        onClick={() => handleMessage(request)}
                        className="flex items-center px-4 py-2 text-gray-700 hover:text-gray-900 bg-gray-100 hover:bg-gray-200 rounded-lg transition-colors"
                      >
                        <MessageCircle className="h-4 w-4 mr-2" />
                        Message
                      </button>
                      <button 
                        onClick={() => handleReject(request.id)}
                        className="flex items-center px-4 py-2 text-red-700 hover:text-red-800 bg-red-100 hover:bg-red-200 rounded-lg transition-colors"
                      >
                        <XCircle className="h-4 w-4 mr-2" />
                        Reject
                      </button>
                      <button 
                        onClick={() => handleApprove(request.id)}
                        className="flex items-center px-4 py-2 text-green-700 hover:text-green-800 bg-green-100 hover:bg-green-200 rounded-lg transition-colors"
                      >
                        <CheckCircle className="h-4 w-4 mr-2" />
                        Approve
                      </button>
                    </div>
                  )}
                </div>
              </div>
            </div>
          ))}

          {filteredRequests.length === 0 && (
            <div className="bg-white rounded-lg shadow-sm p-8 text-center">
              <Calendar className="mx-auto h-12 w-12 text-gray-400 mb-4" />
              <h3 className="text-lg font-medium text-gray-900 mb-2">No Applications Found</h3>
              <p className="text-gray-600">
                {filter === 'pending' 
                  ? 'There are no pending applications to review.'
                  : 'No applications match your search criteria.'}
              </p>
            </div>
          )}
        </div>
      </div>
    </AdminLayout>
  );
};

export default AdminApplications;